import {
  cancelarCobrancaArmazenagem,
  cancelarCobrancaAvulsa,
  cancelarCobrancaEtiquetagem,
  cancelarCobrancaPaletizacao
} from '@spark/services/deposito';
import { formatarCNPJ } from '@spark/utils/formatters';

export const getApiErrorMessage = (error, fallback) => {
  const message = error?.response?.data?.error || error?.response?.data?.message || error?.message;
  return typeof message === 'string' && message.trim() ? message : fallback;
};

export const mapTipoCobranca = (tipoCobranca) => {
  const tipo = String(tipoCobranca || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

  if (tipo === 'paletizacao') return 'paletizacao';
  if (tipo === 'etiquetagem') return 'etiquetagem';
  if (tipo === 'armazenagem') return 'armazenagem';
  if (tipo === 'avulsa') return 'avulsa';
  return '';
};

export const cancelarCobrancaDepositoPorTipo = async (tipoCobranca, protocolo) => {
  const tipo = mapTipoCobranca(tipoCobranca);

  if (!tipo || !protocolo) {
    throw new Error('Tipo de cobranca ou protocolo invalido para cancelamento.');
  }

  if (tipo === 'paletizacao') {
    await cancelarCobrancaPaletizacao(protocolo);
    return;
  }

  if (tipo === 'etiquetagem') {
    await cancelarCobrancaEtiquetagem(protocolo);
    return;
  }

  if (tipo === 'armazenagem') {
    await cancelarCobrancaArmazenagem(protocolo);
    return;
  }

  await cancelarCobrancaAvulsa(protocolo);
};

const formatarDataComHora = (data) => {
  if (!data) return '';

  return new Date(data).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

export const mapCobrancaListItem = (item) => ({
  id: `${item.tipoCobranca || item.TipoCobranca}-${item.numeroPedidoTMS || item.NumeroPedidoTMS}`,
  numeroPedidoTMS: item.numeroPedidoTMS || item.NumeroPedidoTMS || '',
  cnpjCliente: formatarCNPJ(item.cnpjCliente || item.CNPJCliente || ''),
  nomeCliente: item.nomeCliente || item.NomeCliente || '',
  tipoCobranca: item.tipoCobranca || item.TipoCobranca || '',
  numerosNF: item.numerosNF || item.NumerosNF || '',
  protocoloPedidoTMS: item.protocoloPedidoTMS || item.ProtocoloPedidoTMS || '',
  valorTotalEmitido: item.valorTotalEmitido ?? item.ValorTotalEmitido ?? 0,
  quantidadeTotalCaixas: item.quantidadeTotalCaixas ?? item.QuantidadeTotalCaixas ?? 0,
  quantidadeTotalEtiquetas: item.quantidadeTotalEtiquetas ?? item.QuantidadeTotalEtiquetas ?? 0,
  quantidadeTotalPaletes: item.quantidadeTotalPaletes ?? item.QuantidadeTotalPaletes ?? 0,
  usuarioEmissao: item.usuarioEmissao || item.UsuarioEmissao || '',
  dataEmissao: item.dataEmissao || item.DataEmissao ? formatarDataComHora(item.dataEmissao || item.DataEmissao) : '',
  dataEmissaoOriginal: item.dataEmissao || item.DataEmissao || null
});

const toDigitsOnly = (value) => {
  if (value === null || value === undefined) return '';
  return String(value).replace(/\D/g, '');
};

const buildApiDateTime = (value, endOfDay) => {
  if (!value) return undefined;

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return undefined;

    const isoDateOnly = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoDateOnly) {
      return `${trimmed}T${endOfDay ? '23:59:59' : '00:00:00'}`;
    }

    return trimmed;
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;

  const year = String(date.getFullYear());
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const time = endOfDay ? '23:59:59' : '00:00:00';

  return `${year}-${month}-${day}T${time}`;
};

export const normalizeCobrancasDepositoFilters = (filtros) =>
  Object.fromEntries(
    Object.entries(filtros)
      .map(([key, value]) => {
        if (key === 'DataEmissaoInicio') return [key, buildApiDateTime(value, false)];
        if (key === 'DataEmissaoFim') return [key, buildApiDateTime(value, true)];
        if (key === 'CNPJCliente') {
          const digits = toDigitsOnly(value);
          return [key, digits || undefined];
        }
        return [key, value];
      })
      .filter(([, value]) => value !== undefined && value !== null && value !== '')
  );
